import { battleshipGame } from "@topzle/game-battleship";
import { charadesGame } from "@topzle/game-charades";
import { chessGame } from "@topzle/game-chess";
import { connectFourGame } from "@topzle/game-connect-four";
import { drawGuessGame } from "@topzle/game-draw-guess";
import { ludoGame } from "@topzle/game-ludo";
import { mafiaGame } from "@topzle/game-mafia";
import { rockPaperScissorsGame } from "@topzle/game-rock-paper-scissors";
import type { GameManifest, TopzleGameDefinition } from "@topzle/game-sdk";
import { snakesLaddersGame } from "@topzle/game-snakes-ladders";
import { storyMysteryGame } from "@topzle/game-story-mystery";
import { tabooGame } from "@topzle/game-taboo";
import { ticTacToeGame } from "@topzle/game-tic-tac-toe";
import { triviaGame } from "@topzle/game-trivia";
import { truthOrDareGame } from "@topzle/game-truth-or-dare";

type RegisteredGame = TopzleGameDefinition<unknown, unknown, unknown, unknown>;

export const gameDefinitions = [
  ticTacToeGame,
  chessGame,
  ludoGame,
  connectFourGame,
  battleshipGame,
  drawGuessGame,
  rockPaperScissorsGame,
  snakesLaddersGame,
  mafiaGame,
  charadesGame,
  truthOrDareGame,
  tabooGame,
  triviaGame,
  storyMysteryGame,
] as const;

export const gameCatalog: GameManifest[] = gameDefinitions.map((game) => ({
  id: game.id,
  version: game.version,
  name: game.name,
  description: game.shortDescription,
  category: game.category,
  icon: game.icon,
  playerCount:
    game.minPlayers === game.maxPlayers
      ? `${game.minPlayers}`
      : `${game.minPlayers}–${game.maxPlayers}`,
  online: game.supportsOnlinePlay,
  local: game.supportsLocalPlay,
  ai: game.supportsLocalAi || game.supportsCloudAi || game.supportsAiPlayers,
  media: game.requiredCapabilities.includes("video")
    ? "video"
    : game.requiredCapabilities.includes("audio")
      ? "voice"
      : "either",
  status: game.id === "story-mystery" ? "beta" : "available",
}));

export const playableRoomGameIds = [
  "tic-tac-toe",
  "chess",
  "ludo",
  "connect-four",
  "battleship",
  "draw-guess",
  "rock-paper-scissors",
  "snakes-ladders",
  "mafia",
] as const;
export type PlayableRoomGameId = (typeof playableRoomGameIds)[number];

export const playableRoomGameIdSet = new Set<string>(playableRoomGameIds);
export const roomGameCatalog = playableRoomGameIds.map((id) => {
  const game = gameCatalog.find((entry) => entry.id === id);
  if (!game) throw new Error(`Playable room game "${id}" is missing from the game registry`);
  return { ...game, id };
});

export const roomGameSymbols: Record<PlayableRoomGameId, string> = {
  "tic-tac-toe": "XO",
  chess: "♞",
  ludo: "⚄",
  "connect-four": "●●",
  battleship: "⚓",
  "draw-guess": "✎",
  "rock-paper-scissors": "✊✋",
  "snakes-ladders": "🐍",
  mafia: "🎭",
};

export const roomGameThemes: Record<PlayableRoomGameId, readonly string[]> = {
  "tic-tac-toe": ["Showdown", "Grid Party", "Triple Threat"],
  chess: ["Knight Club", "Crown Clash", "Checkmate Lounge"],
  ludo: ["Dice Dash", "Victory Lap", "Home Run"],
  "connect-four": ["Drop Zone", "Fourfront", "Disc District"],
  battleship: ["High Seas", "Fleet Command", "Sonar Station"],
  "draw-guess": ["Doodle Dash", "Sketch Sprint", "Canvas Chaos"],
  "rock-paper-scissors": ["Hand Showdown", "Three Count Clash", "Throwdown"],
  "snakes-ladders": ["Serpent Sprint", "Ladder Legends", "Race to 100"],
  mafia: ["Midnight Mystery", "Neon Noir", "Town of Secrets"],
};

export const roomGameNames = Object.fromEntries(
  roomGameCatalog.map((game) => [game.id, game.name]),
) as Record<PlayableRoomGameId, string>;

export function funRoomName(
  displayName: string,
  gameId: PlayableRoomGameId,
  randomValue = Math.floor(Math.random() * 0x1_0000_0000),
) {
  const name = displayName.trim().split(/\s+/)[0]?.slice(0, 28) || "Player";
  const possessive = name.toLowerCase().endsWith("s") ? `${name}'` : `${name}'s`;
  const themes = roomGameThemes[gameId];
  const theme = themes[Math.abs(randomValue) % themes.length] ?? "Showdown";
  return `${possessive} ${roomGameNames[gameId]} ${theme}`.slice(0, 80);
}

export function getGameDefinition(gameId: string): RegisteredGame | null {
  return (gameDefinitions.find((game) => game.id === gameId) as RegisteredGame | undefined) ?? null;
}
